import { fmtMoney } from "@/lib/format";
import { Briefcase, Landmark, Plus, Target, Trash2 } from "lucide-react";
import { Eyebrow, monthLabel, MonthDateInput } from "./ui";

const inp = "border px-2 py-1 text-xs font-mono-fin bg-white";
const bc = { borderColor: "var(--ll-border)" };
const uid = () => Math.random().toString(36).slice(2, 10);
const runwayLabel = (m) => (m == null ? "36+ mo" : `${m} mo`);

export const newItem = (type, month) => (type === "hire"
  ? { id: uid(), type, label: "New hire", enabled: true, start_month: month, monthly_salary: 5000, burden_pct: 20 }
  : { id: uid(), type, label: "Loan", enabled: true, start_month: month, amount: 25000, apr: 9, term_months: 36 });

export const cleanItems = (items) => (items || [])
  .filter((x) => /^\d{4}-\d{2}$/.test(x.start_month))
  .map((x) => ({
    ...x,
    monthly_salary: Number(x.monthly_salary) || 0, burden_pct: Number(x.burden_pct) || 0,
    amount: Number(x.amount) || 0, apr: Number(x.apr) || 0, term_months: parseInt(x.term_months, 10) || 0,
    monthly_amount: Number(x.monthly_amount) || 0, end_month: /^\d{4}-\d{2}$/.test(x.end_month || "") ? x.end_month : null,
  }));

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-0.5"><span className="text-[9px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{label}</span>{children}</label>
);

export default function WhatIfPlanner({ items, setItems, plan, firstMonth }) {
  const set = (i, k, v) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const impact = (id) => plan?.items?.find((x) => x.id === id);
  return (
    <div className="mt-6 pt-5 border-t" style={bc} data-testid="whatif-planner">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <Eyebrow>What-if plan · internal only</Eyebrow>
          <div className="font-display font-bold text-lg">Hires, loans & goal plans</div>
          <div className="text-xs text-[var(--ll-ink-soft)]">Applied to the Base case. Toggle items to compare.</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setItems([...items, newItem("hire", firstMonth)])} className="text-xs px-2.5 py-1.5 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1" style={bc} data-testid="whatif-add-hire"><Plus size={12} /><Briefcase size={12} /> New hire</button>
          <button onClick={() => setItems([...items, newItem("loan", firstMonth)])} className="text-xs px-2.5 py-1.5 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1" style={bc} data-testid="whatif-add-loan"><Plus size={12} /><Landmark size={12} /> Loan / credit line</button>
        </div>
      </div>
      {items.length === 0 && <div className="text-sm text-[var(--ll-ink-soft)]" data-testid="whatif-empty">Add a planned hire or loan to see how it changes runway and credit needs.</div>}
      <ul className="space-y-2">
        {items.map((x, i) => {
          const im = impact(x.id);
          return (
            <li key={x.id} className={`border p-3 flex flex-wrap items-end gap-3 ${x.enabled ? "" : "opacity-50"}`} style={bc} data-testid={`whatif-item-${i}`}>
              <label className="flex items-center gap-1.5 text-xs self-center">
                <input type="checkbox" checked={x.enabled} onChange={(e) => set(i, "enabled", e.target.checked)} data-testid={`whatif-toggle-${i}`} />
                {x.type === "hire" ? <Briefcase size={13} /> : x.type === "goal" ? <Target size={13} /> : <Landmark size={13} />}
              </label>
              <Field label="Label"><input value={x.label} onChange={(e) => set(i, "label", e.target.value)} className={`${inp} w-32 font-sans`} style={bc} data-testid={`whatif-label-${i}`} /></Field>
              <Field label={x.type === "loan" ? "Received" : "Start"}><MonthDateInput value={x.start_month} onChange={(v) => set(i, "start_month", v)} edge="start" allowEmpty={false} className={`${inp} w-[100px]`} style={bc} testid={`whatif-start-${i}`} /></Field>
              {x.type === "goal" ? (
                <>
                  <Field label={x.goal_kind === "debt" ? "Extra debt pmt / mo" : "Extra saved / mo"}><input type="number" min="0" value={x.monthly_amount} onChange={(e) => set(i, "monthly_amount", e.target.value)} className={`${inp} w-28`} style={bc} data-testid={`whatif-goal-amount-${i}`} /></Field>
                  <Field label="Until"><MonthDateInput value={x.end_month || ""} onChange={(v) => set(i, "end_month", v)} className={`${inp} w-[100px]`} style={bc} testid={`whatif-goal-end-${i}`} /></Field>
                  <span className="text-[10px] text-[var(--ll-ink-muted)] self-center max-w-[160px]">{x.goal_kind === "debt" ? "Cash out; also added to the debt plan's extra payment" : "Cash in from more income / less spending"}</span>
                </>
              ) : x.type === "hire" ? (
                <>
                  <Field label="Salary / mo"><input type="number" min="0" value={x.monthly_salary} onChange={(e) => set(i, "monthly_salary", e.target.value)} className={`${inp} w-24`} style={bc} data-testid={`whatif-salary-${i}`} /></Field>
                  <Field label="Burden %"><input type="number" min="0" value={x.burden_pct} onChange={(e) => set(i, "burden_pct", e.target.value)} className={`${inp} w-16`} style={bc} data-testid={`whatif-burden-${i}`} /></Field>
                </>
              ) : (
                <>
                  <Field label="Amount"><input type="number" min="0" value={x.amount} onChange={(e) => set(i, "amount", e.target.value)} className={`${inp} w-24`} style={bc} data-testid={`whatif-amount-${i}`} /></Field>
                  <Field label="APR %"><input type="number" min="0" value={x.apr} onChange={(e) => set(i, "apr", e.target.value)} className={`${inp} w-16`} style={bc} data-testid={`whatif-apr-${i}`} /></Field>
                  <Field label="Term (mo)"><input type="number" min="0" value={x.term_months} onChange={(e) => set(i, "term_months", e.target.value)} className={`${inp} w-16`} style={bc} data-testid={`whatif-term-${i}`} /></Field>
                </>
              )}
              {im && (
                <div className="text-[11px] leading-snug ml-auto text-right" data-testid={`whatif-impact-${i}`}>
                  <div className="font-mono-fin">{fmtMoney(im.monthly_effect)}/mo</div>
                  <div>Runway {runwayLabel(plan.without.runway_months)} → <b>{runwayLabel(im.runway_months)}</b></div>
                  <div style={{ color: im.credit_delta > 0 ? "var(--ll-error)" : "var(--ll-success)" }}>
                    {im.credit_delta > 0 ? `+${fmtMoney(im.credit_delta)} credit need` : im.credit_delta < 0 ? `${fmtMoney(im.credit_delta)} credit need` : "No change to credit need"}
                  </div>
                </div>
              )}
              <button onClick={() => setItems(items.filter((_, j) => j !== i))} className={`p-1.5 border hover:bg-[#FEE2E2] ${im ? "" : "ml-auto"}`} style={bc} data-testid={`whatif-remove-${i}`}><Trash2 size={12} /></button>
            </li>
          );
        })}
      </ul>
      {plan && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4" data-testid="whatif-summary">
          <Stat label="Runway (base)" testid="whatif-runway" before={runwayLabel(plan.without.runway_months)} after={runwayLabel(plan.with.runway_months)} bad={plan.runway_delta < 0} />
          <Stat label="Lowest cash (12 mo)" testid="whatif-min-cash" before={fmtMoney(plan.without.min_cash)} after={fmtMoney(plan.with.min_cash)} bad={plan.with.min_cash < plan.without.min_cash} />
          <Stat label="Cash / credit needed" testid="whatif-credit" before={fmtMoney(plan.without.credit_needed)} after={fmtMoney(plan.with.credit_needed)} bad={plan.credit_delta > 0} />
        </div>
      )}
      {plan?.with?.first_shortfall_month && (
        <div className="text-xs mt-2 text-[var(--ll-error)]" data-testid="whatif-shortfall">With this plan, cash drops below your buffer by {monthLabel(plan.with.first_shortfall_month)}.</div>
      )}
    </div>
  );
}

const Stat = ({ label, before, after, bad, testid }) => (
  <div className="border p-3" style={{ ...bc, borderTop: `3px solid ${bad ? "var(--ll-error)" : "var(--ll-success)"}` }} data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{label}</div>
    <div className="text-sm font-mono-fin mt-1"><span className="opacity-60">{before}</span> → <b style={{ color: bad ? "var(--ll-error)" : "var(--ll-success)" }}>{after}</b></div>
  </div>
);
